import { useAIAssessmentContext } from "../../../Context/AIAssessmentContext/AIAssessmentContext";
import { openNotification } from "../../../Components/Notification/Index";
import InterviewService from "../../../Services/interview.service";
import { useLoader } from "../../../Context/LoaderContext/LoaderContext";
import { getFingerprint } from "@thumbmarkjs/thumbmarkjs";
import aiInterviewService from "../../../Services/ai-interview.service";
import usePollingFunctions from "./usePollingFunctions";
import { Question } from "../../../Interface/Index";
import { AIAssessmentQuestion } from "../../../Entities/ai-assessment.entity";
import { TIMER_CONSTANTS } from "../constants/interview-constants";

interface UseAiAssessmentStartReturnType {
  toggleToFullscreenMode: () => Promise<boolean>;
  toggleToNormalScreenMode: () => Promise<void>;
  handleStartAiAssessment: () => Promise<void>;
  handlePermissionCancel: () => void;
  handlePermissionAllow: () => Promise<void>;
  handleStartInterviewCancel: (
    type: "devTools" | "permission" | "other"
  ) => Promise<void>;
  handleStartInterviewConfirm: () => Promise<void>;
}

const useAiAssessmentStart = (): UseAiAssessmentStartReturnType => {
  const {
    fullscreenElementRef,
    isFullscreen,
    setIsFullscreen,
    currentSession,
    setCurrentSession,
    setShowPermissionConfirmationModal,
    cameraMicrophoneStreamRef,
    displayStreamRef,
    mediaRecorderRef,
    setRecordedBlob,
    recordedChunks,
    setShowStartAiAssessmentScreen,
    setAiAssessmentQuestions,
    setAiAssessmentDetails,
    setShowCamRecorder,
    setCurrentStep,
    setAssessmentStartTime,
  } = useAIAssessmentContext();

  const { checkForNextRoundQuestions } = usePollingFunctions();

  const { setLoading, setLoadingMessage, clearLoader } = useLoader();

  const interviewService = new InterviewService();

  const toggleToFullscreenMode = async (): Promise<boolean> => {
    if (!fullscreenElementRef.current) {
      console.error("Element reference is null - cannot enter fullscreen");
      return false;
    }

    try {
      const element = fullscreenElementRef.current;

      // Check if fullscreen is supported
      const isFullscreenSupported = !!(
        element.requestFullscreen ||
        (element as any).mozRequestFullScreen ||
        (element as any).webkitRequestFullscreen ||
        (element as any).msRequestFullscreen
      );

      if (!isFullscreenSupported) {
        console.error(
          "useAiAssessmentStart: Fullscreen API is not supported on this browser"
        );
        return false;
      }

      // Check if document has focus and user activation
      if (!document.hasFocus()) {
        console.error(
          "useAiAssessmentStart: Document doesn't have focus - fullscreen will likely fail"
        );
        return false;
      }

      // checking element is already in fullscreen mode
      if (
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      ) {
        console.log("useAiAssessmentStart: Already in fullscreen mode");
        setIsFullscreen(true);
        return true;
      }

      // Try to request fullscreen with proper error handling
      let fullscreenPromise: Promise<void> | undefined;

      if (element.requestFullscreen) {
        fullscreenPromise = element.requestFullscreen();
      } else if ((element as any).mozRequestFullScreen) {
        fullscreenPromise = (element as any).mozRequestFullScreen();
      } else if ((element as any).webkitRequestFullscreen) {
        fullscreenPromise = (element as any).webkitRequestFullscreen();
      } else if ((element as any).msRequestFullscreen) {
        fullscreenPromise = (element as any).msRequestFullscreen();
      }

      if (fullscreenPromise) {
        await fullscreenPromise;
        console.log(
          "useAiAssessmentStart: Successfully entered fullscreen mode"
        );
        setIsFullscreen(true);
        return true;
      }

      return false;
    } catch (error: any) {
      console.error(
        "useAiAssessmentStart: Failed to enter fullscreen mode:",
        error
      );
      if (error.name === "NotAllowedError") {
        console.error(
          "useAiAssessmentStart: Fullscreen was blocked by browser - continuing without fullscreen"
        );
      } else if (error.name === "TypeError") {
        console.error(
          "useAiAssessmentStart: Fullscreen is not available - continuing without fullscreen"
        );
      }

      return false;
    }
  };

  const toggleToNormalScreenMode = async (): Promise<void> => {
    if (!isFullscreen) {
      console.error(
        "useAiAssessmentStart: Not in fullscreen mode - cannot exit fullscreen"
      );
      return;
    }

    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        await (document as any).mozCancelFullScreen();
      } else if ((document as any).webkitExitFullscreen) {
        await (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        await (document as any).msExitFullscreen();
      }
      console.log("useAiAssessmentStart: Successfully exited fullscreen mode");
      setIsFullscreen(false);
    } catch (error) {
      console.error(
        "useAiAssessmentStart: Failed to exit fullscreen mode:",
        error
      );
    }
  };

  const stopStreamTracks = (stream: MediaStream | null) => {
    if (!stream) return;
    try {
      stream.getTracks().forEach((track) => track.stop());
    } catch {}
  };

  const startScreenRecording = async () => {
    try {
      recordedChunks.current = [];
      console.log("[Step 10] startScreenRecording: cleared recordedChunks", { length: recordedChunks.current.length });

      let screenStream: MediaStream;
      const existingDisplay = displayStreamRef.current;
      if (existingDisplay) {
        const videoTrack = existingDisplay.getVideoTracks()[0];
        const videoEnded = !videoTrack || videoTrack.readyState === "ended";
        console.log("[Step 11] startScreenRecording: displayStreamRef exists", {
          videoTrackReadyState: videoTrack?.readyState ?? "no-track",
          videoEnded,
        });
        if (videoEnded) {
          console.log("[Step 12] startScreenRecording: display stream video track ended, getting new getDisplayMedia");
          displayStreamRef.current = null;
          screenStream = await navigator.mediaDevices.getDisplayMedia({
            video: {
              frameRate: { max: 15 },
              width: { max: 1920 },
              height: { max: 1080 },
              ...({ cursor: "always" } as any),
            } as MediaTrackConstraints,
            audio: true,
          });
          displayStreamRef.current = screenStream;
          console.log("[Step 13] startScreenRecording: new display stream acquired", {
            trackCount: screenStream.getTracks().length,
          });
        } else {
          screenStream = existingDisplay;
          console.log("[Step 14] startScreenRecording: reusing existing live display stream");
        }
      } else {
        console.log("[Step 15] startScreenRecording: no display stream in context, calling getDisplayMedia");
        screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            frameRate: { max: 15 },
            width: { max: 1920 },
            height: { max: 1080 },
            ...({ cursor: "always" } as any),
          } as MediaTrackConstraints,
          audio: true,
        });
        displayStreamRef.current = screenStream;
      }

      console.log("[Step 16] startScreenRecording: validating entire screen share");
      if (!isEntireScreenShare(screenStream)) {
        openNotification(
          "error",
          "Select Entire Screen",
          "Please share your 'Entire Screen' to start screen recording."
        );
        stopStreamTracks(screenStream);
        displayStreamRef.current = null;
        return;
      }

      // Do not applyConstraints() to the display track: it can cause
      // "The track is not connected to any source" and result in 2x2 video
      // dimensions and no encoder output. Use the stream as-is; canvas scales to target size.

      const targetWidth = 854; // 480p width
      const targetHeight = 480; // 480p height
      const targetFps = 15;

      const videoEl = document.createElement("video");
      videoEl.srcObject = screenStream;
      videoEl.muted = true;
      (videoEl as any).playsInline = true;

      videoEl.addEventListener("playing", () => {
        console.log("[Step 17] startScreenRecording: video element started playing", {
          readyState: videoEl.readyState,
          videoWidth: videoEl.videoWidth,
          videoHeight: videoEl.videoHeight,
        });
      }, { once: true });

      try {
        await new Promise<void>((resolve) => {
          videoEl.onloadedmetadata = () => resolve();
        });
        await videoEl.play();
        console.log("[Step 17b] startScreenRecording: videoEl.play() resolved", {
          readyState: videoEl.readyState,
          videoWidth: videoEl.videoWidth,
          videoHeight: videoEl.videoHeight,
        });
      } catch (e) {
        console.warn("[Step 17b] startScreenRecording: could not autoplay screen video element", e);
      }

      const canvas = document.createElement("canvas");
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext("2d");
      let rafId: number | null = null;
      let rVFCancelId: number | null = null;
      let firstFrameDrawn = false;

      const drawWithRAF = () => {
        try {
          if (ctx) {
            ctx.drawImage(videoEl, 0, 0, targetWidth, targetHeight);
            if (!firstFrameDrawn) {
              firstFrameDrawn = true;
              console.log("[Step 18] startScreenRecording: first canvas frame drawn (RAF)", {
                videoReadyState: videoEl.readyState,
                videoWidth: videoEl.videoWidth,
                videoHeight: videoEl.videoHeight,
              });
            }
          }
          rafId = requestAnimationFrame(drawWithRAF);
        } catch (_) {
          rafId = requestAnimationFrame(drawWithRAF);
        }
      };

      const startRVFC = () => {
        const cb = () => {
          try {
            if (ctx) {
              ctx.drawImage(videoEl, 0, 0, targetWidth, targetHeight);
              if (!firstFrameDrawn) {
                firstFrameDrawn = true;
                console.log("[Step 18] startScreenRecording: first canvas frame drawn (requestVideoFrameCallback)", {
                  videoReadyState: videoEl.readyState,
                  videoWidth: videoEl.videoWidth,
                  videoHeight: videoEl.videoHeight,
                });
              }
            }
          } finally {
            rVFCancelId = (videoEl as any).requestVideoFrameCallback
              ? (videoEl as any).requestVideoFrameCallback(cb)
              : null;
          }
        };
        rVFCancelId = (videoEl as any).requestVideoFrameCallback
          ? (videoEl as any).requestVideoFrameCallback(cb)
          : null;
      };

      if ((videoEl as any).requestVideoFrameCallback) {
        startRVFC();
      } else {
        drawWithRAF();
      }

      const canvasStream = canvas.captureStream(targetFps);

      const audioTracks: MediaStreamTrack[] = [];
      
      const systemAudioTracks = screenStream.getAudioTracks();
      if (systemAudioTracks.length > 0) {
        audioTracks.push(...systemAudioTracks);
        console.log("[Step 19] startScreenRecording: system audio tracks added to recording");
      }
      if (cameraMicrophoneStreamRef.current) {
        const micTracks = cameraMicrophoneStreamRef.current.getAudioTracks();
        const micLive = micTracks.filter((t) => t.readyState === "live");
        if (micLive.length > 0) {
          audioTracks.push(...micLive);
          console.log("[Step 19] startScreenRecording: microphone audio tracks added", { count: micLive.length });
        } else {
          console.log("[Step 19] startScreenRecording: camera/mic ref exists but no live audio tracks", {
            total: micTracks.length,
            states: micTracks.map((t) => t.readyState),
          });
        }
      } else {
        console.log("[Step 19] startScreenRecording: no camera/mic stream in context for recording");
      }
      if (audioTracks.length === 0) {
        console.warn("[Step 19] startScreenRecording: no audio tracks available for recording");
      }

      const combinedStream = new MediaStream([
        ...canvasStream.getVideoTracks(),
        ...audioTracks,
      ]);

      const supportedMimeType = ((): string => {
        if (
          (window as any).MediaRecorder &&
          MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
        )
          return "video/webm;codecs=vp9";
        if (
          (window as any).MediaRecorder &&
          MediaRecorder.isTypeSupported("video/webm;codecs=vp8")
        )
          return "video/webm;codecs=vp8";
        return "video/webm";
      })();

      const mediaRecorder = new MediaRecorder(combinedStream, {
        mimeType: supportedMimeType,
        videoBitsPerSecond: 400000,
        audioBitsPerSecond: 48000,
      });
      mediaRecorderRef.current = mediaRecorder;
      console.log("[Step 20] startScreenRecording: MediaRecorder created", {
        mimeType: supportedMimeType,
        videoTracks: combinedStream.getVideoTracks().length,
        audioTracks: combinedStream.getAudioTracks().length,
        streamId: combinedStream.id,
      });

      mediaRecorder.ondataavailable = (event) => {
        const size = event.data?.size ?? 0;
        const chunksLengthBefore = recordedChunks.current.length;
        if (event.data.size > 0) {
          recordedChunks.current.push(event.data);
        }
        const chunksLengthAfter = recordedChunks.current.length;
        console.log("[Step 21] startScreenRecording: ondataavailable", {
          size,
          pushed: size > 0,
          chunksLengthBefore,
          chunksLengthAfter,
        });
      };

      mediaRecorder.onstop = () => {
        const chunksCount = recordedChunks.current.length;
        const chunksTotalBytes = recordedChunks.current.reduce((sum, c) => sum + (c?.size ?? 0), 0);
        console.log("[Step 22] startScreenRecording: MediaRecorder onstop", {
          chunksCount,
          chunksTotalBytes,
          chunkSizes: recordedChunks.current.map((c) => c?.size ?? 0),
        });
        const blob = new Blob(recordedChunks.current, { type: "video/webm" });
        setRecordedBlob(blob);
        console.log("[Step 22b] startScreenRecording: blob created from chunks", { size: blob.size, type: blob.type });
        stopStreamTracks(screenStream);
        try {
          canvasStream.getTracks().forEach((t) => t.stop());
          if (rafId !== null) {
            cancelAnimationFrame(rafId);
            rafId = null;
          }
          if (
            rVFCancelId !== null &&
            (videoEl as any).cancelVideoFrameCallback
          ) {
            (videoEl as any).cancelVideoFrameCallback(rVFCancelId);
            rVFCancelId = null;
          }
        } catch (error) {
          console.warn("Error cleaning up animation resources on stop:", error);
        }

        displayStreamRef.current = null;
      };

      screenStream.getVideoTracks().forEach((track) => {
        track.addEventListener("ended", () => {
          openNotification(
            "error",
            "Screen sharing ended",
            "Screen recording stopped because screen sharing ended."
          );

          if (mediaRecorder.state !== "inactive") {
            mediaRecorder.stop();
          }

          try {
            if (rafId !== null) {
              cancelAnimationFrame(rafId);
              rafId = null;
            }
            if (
              rVFCancelId !== null &&
              (videoEl as any).cancelVideoFrameCallback
            ) {
              (videoEl as any).cancelVideoFrameCallback(rVFCancelId);
              rVFCancelId = null;
            }
            canvasStream.getTracks().forEach((t) => t.stop());
          } catch (error) {
            console.warn("Error cleaning up animation resources:", error);
          }
          try {
            displayStreamRef.current = null;
            mediaRecorderRef.current = null;
            if (recordedChunks?.current) {
              recordedChunks.current = [];
            }
            setRecordedBlob(null);
          } catch (error) {
            console.warn("Error clearing recording state:", error);
          }
        });
      });

      // Use timeslice (e.g. 1000ms) so ondataavailable fires periodically; without it many browsers only fire on stop()
      const RECORD_TIMESLICE_MS = 1000;
      (mediaRecorder as any)._startTime = Date.now();
      mediaRecorder.start(RECORD_TIMESLICE_MS);
      console.log("[Step 23] startScreenRecording: MediaRecorder.start() called", {
        timesliceMs: RECORD_TIMESLICE_MS,
        state: mediaRecorder.state,
      });
    } catch (err) {
      console.error("[useAiAssessmentStart] Error starting screen recording:", err);
      openNotification(
        "error",
        "Screen recording failed",
        "We could not start screen recording. Please allow screen sharing and select 'Entire Screen'."
      );
    }
  };

  const isEntireScreenShare = (displayStream: MediaStream): boolean => {
    try {
      const videoTrack = displayStream.getVideoTracks()[0];
      if (!videoTrack) return false;
      const settings = videoTrack.getSettings() as MediaTrackSettings & {
        displaySurface?: string;
      };
      const displaySurface: string | undefined = (settings as any)
        .displaySurface;
      if (typeof displaySurface === "string") {
        return displaySurface === "monitor"; // "monitor" corresponds to Entire Screen
      }
      const label = (videoTrack.label || "").toLowerCase();
      if (
        label.includes("window") ||
        label.includes("tab") ||
        label.includes("application") ||
        label.includes("browser")
      ) {
        return false;
      }
      if (label.includes("screen")) {
        return true;
      }
      // Fallback: if we can't positively identify, be conservative and require re-selection
      return false;
    } catch {
      return false;
    }
  };

  const handleStartAiAssessment = async () => {
    setLoading(true);
    setLoadingMessage("Starting AI Assessment...");

    if (!currentSession) {
      console.error(
        "useAiAssessmentStart: No session found -> handleStartAiAssessment"
      );
      openNotification(
        "error",
        "No session found",
        "The interview session could not be found. Please refresh the page."
      );
      clearLoader();
      return;
    }

    const latestSession = await interviewService.findSessionById(
      currentSession._id.toString()
    );

    if (!latestSession) {
      console.error(
        "useAiAssessmentStart: Session not found on server -> handleStartAiAssessment"
      );
      openNotification(
        "error",
        "Session not found",
        "The interview session could not be found. Please refresh the page."
      );
      clearLoader();
      return;
    }

    setCurrentSession(latestSession);

    if (latestSession.isLive) {
      console.error(
        "useAiAssessmentStart: Session is already live -> handleStartAiAssessment"
      );
      openNotification(
        "error",
        "Session already started on another device",
        "The interview session has already been started on another device. Please refresh the page."
      );
      clearLoader();
      return;
    }

    if (latestSession.status !== "pending") {
      console.error(
        "useAiAssessmentStart: Session is not pending -> handleStartAiAssessment"
      );
      openNotification(
        "error",
        "Session is not pending",
        "The interview session is not pending. Please refresh the page."
      );
      clearLoader();
      return;
    }

    if (
      !latestSession.isQuestionsGenerated ||
      latestSession?.noOfQuestionsGenerated === 0
    ) {
      console.error(
        "useAiAssessmentStart: Questions are not generated -> handleStartAiAssessment"
      );
      openNotification(
        "error",
        "Questions are not generated",
        "The interview questions could not be found. Please refresh the page."
      );
      clearLoader();
      return;
    }

    setShowPermissionConfirmationModal(true);
    clearLoader();
  };

  const handlePermissionCancel = () => {
    if (cameraMicrophoneStreamRef.current) {
      stopStreamTracks(cameraMicrophoneStreamRef.current);
      cameraMicrophoneStreamRef.current = null;
    }
    if (displayStreamRef.current) {
      stopStreamTracks(displayStreamRef.current);
      displayStreamRef.current = null;
    }
    setShowPermissionConfirmationModal(false);
    openNotification(
      "error",
      "Permission denied",
      "Please allow all permissions to continue with the interview."
    );
  };

  const handlePermissionAllow = async () => {
    let stream: MediaStream | null = null;
    let displayStream: MediaStream | null = null;

    // Camera and Microphone Permission
    try {
      const cameraPermission = await navigator.permissions.query({
        name: "camera" as PermissionName,
      });
      const microphonePermission = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      });

      if (cameraPermission.state === "denied") {
        openNotification(
          "error",
          "Camera access blocked",
          "Camera access is blocked. Please enable it from your browser settings and refresh the page."
        );
        setShowPermissionConfirmationModal(false);
        return;
      }

      if (microphonePermission.state === "denied") {
        openNotification(
          "error",
          "Microphone access blocked",
          "Microphone access is blocked. Please enable it from your browser settings and refresh the page."
        );
        setShowPermissionConfirmationModal(false);
        return;
      }

      stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });

      if (!stream) {
        openNotification(
          "error",
          "Failed to access camera and microphone",
          "Failed to access camera and microphone. Please try again."
        );
        setShowPermissionConfirmationModal(false);
        return;
      }

      cameraMicrophoneStreamRef.current = stream;
    } catch (error: any) {
      console.error(
        "useAiAssessmentStart: Failed to allow permissions:",
        error
      );
      openNotification(
        "error",
        "Permission denied",
        "Please allow all permissions to continue with the interview."
      );
      setShowPermissionConfirmationModal(false);
      stopStreamTracks(stream);
      return;
    }

    // Screen Sharing Permission
    try {
      if (!displayStreamRef.current) {
        displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            frameRate: { max: 15 },
            width: { max: 1920 },
            height: { max: 1080 },
            ...({ cursor: "always" } as any),
          } as MediaTrackConstraints,
          audio: true, // Request system audio
        });
        displayStreamRef.current = displayStream;
      } else {
        displayStream = displayStreamRef.current;
      }
    } catch (error: any) {
      console.error(
        "useAiAssessmentStart: Failed to allow Screen Sharing Permission:",
        error
      );
      openNotification(
        "error",
        "Screen Sharing Permission denied",
        "Please allow screen sharing permission to continue with the interview."
      );
      stopStreamTracks(stream);
      return;
    }

    // Verify system audio is granted - REQUIRED
    const audioTracks = displayStream.getAudioTracks();
    const hasSystemAudio = audioTracks.length > 0 && audioTracks.some(track => track.readyState === 'live' && track.enabled);
    
    if (!hasSystemAudio) {
      // System audio is required - block the start
      openNotification(
        "error",
        "System Audio Required",
        "System audio is required for screen recording. Please click 'Allow All' again and make sure to enable 'Share system audio' or 'Share tab audio' when prompted."
      );

      // Stop all media streams and clean up state
      if (cameraMicrophoneStreamRef.current) {
        stopStreamTracks(cameraMicrophoneStreamRef.current);
        cameraMicrophoneStreamRef.current = null;
      }

      if (displayStreamRef.current) {
        stopStreamTracks(displayStreamRef.current);
        displayStreamRef.current = null;
      }

      // Stop any active media recorder
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current = null;
      }

      // Clear recorded chunks and blob
      if (recordedChunks?.current) {
        recordedChunks.current = [];
      }
      setRecordedBlob(null);

      // Keep the modal open so user can try again - same behavior as other cancelled permissions
      setShowPermissionConfirmationModal(true);
      return;
    } else {
      console.log("useAiAssessmentStart: System audio successfully granted");
    }

    const isEntire = isEntireScreenShare(displayStream);
    if (!isEntire) {
      openNotification(
        "error",
        "Select Entire Screen",
        "Please share your 'Entire Screen' (not a window or tab) and try again."
      );

      // Stop all media streams and clean up state
      if (cameraMicrophoneStreamRef.current) {
        stopStreamTracks(cameraMicrophoneStreamRef.current);
        cameraMicrophoneStreamRef.current = null;
      }

      if (displayStreamRef.current) {
        stopStreamTracks(displayStreamRef.current);
        displayStreamRef.current = null;
      }

      // Stop any active media recorder
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current = null;
      }

      // Clear recorded chunks and blob
      if (recordedChunks?.current) {
        recordedChunks.current = [];
      }
      setRecordedBlob(null);

      setShowPermissionConfirmationModal(false);
      return;
    }

    setShowPermissionConfirmationModal(false);
    setShowStartAiAssessmentScreen(true);
  };

  const handleStartInterviewCancel = async (
    type: "devTools" | "permission" | "other"
  ) => {
    if (cameraMicrophoneStreamRef.current) {
      stopStreamTracks(cameraMicrophoneStreamRef.current);
      cameraMicrophoneStreamRef.current = null;
    }
    if (displayStreamRef.current) {
      stopStreamTracks(displayStreamRef.current);
      displayStreamRef.current = null;
    }
    setShowStartAiAssessmentScreen(false);
    let message =
      "Interview has been cancelled. You can start when you're ready.";
    if (type === "devTools") {
      message =
        "DevTools have been opened. Please close them to start the interview.";
    } else if (type === "permission") {
      message =
        "Permission denied. Please allow all permissions to continue with the interview.";
    } else if (type === "other") {
      message =
        "Interview has been cancelled. You can start when you're ready.";
    }
    openNotification("error", "Interview cancelled", message);
    return;
  };

  const isDevToolsOpened = (): boolean => {
    const threshold = 150;

    return (
      window.outerWidth - window.innerWidth > threshold ||
      window.outerHeight - window.innerHeight > threshold
    );
  };

  const handleStartInterviewConfirm = async () => {
    // Checking if session is found
    if (!currentSession) {
      console.error(
        "useAiAssessmentStart: No session found -> handleStartInterviewConfirm"
      );
      setShowStartAiAssessmentScreen(false);
      return;
    }

    // Checking if DevTools are opened
    if (isDevToolsOpened()) {
      setShowStartAiAssessmentScreen(false);
      await handleStartInterviewCancel("devTools");
      return;
    }

    setLoading(true);
    setLoadingMessage("Starting interview please wait...");
    try {
      // Getting Browser Fingerprint
      const { hash } = await getFingerprint(true);
      if (!hash) {
        console.error(
          "useAiAssessmentStart: No hash found -> handleStartInterviewConfirm"
        );
        openNotification(
          "error",
          "Interview start failed",
          "Failed to start the interview. Please try again."
        );
        clearLoader();
        await handleStartInterviewCancel("other");
        return;
      }

      // Marking session as live and generating session token
      let updatedSession = await aiInterviewService.startAIInterview(
        currentSession._id,
        hash
      );
      if (!updatedSession) {
        console.error(
          "useAiAssessmentStart: Failed to start interview -> handleStartInterviewConfirm"
        );
        openNotification(
          "error",
          "Interview start failed",
          "Failed to start the interview. Please try again."
        );
        clearLoader();
        await handleStartInterviewCancel("other");
        return;
      }

      // Loading Questions from the server
      const sessionQuestions: Question[] = [];
      if (!updatedSession.isQuestionsGenerated) {
        setLoadingMessage("Generating questions, please wait...");
        updatedSession = await checkForNextRoundQuestions(updatedSession._id);
        if (!updatedSession) {
          console.error(
            "useAiAssessmentStart: Failed to generate questions -> handleStartInterviewConfirm"
          );
          openNotification(
            "error",
            "Questions generation failed",
            "Failed to generate questions. Please try again."
          );
          clearLoader();
          await handleStartInterviewCancel("other");
          return;
        }

        const questions =
          await aiInterviewService.getQuestionsBySessionIdAndSessionToken(
            updatedSession._id,
            updatedSession?.sessionToken
          );

        if (!questions || questions.length === 0) {
          console.error(
            "useAiAssessmentStart: No questions found -> handleStartInterviewConfirm"
          );
          openNotification(
            "error",
            "Questions not found",
            "No questions found. Please try again."
          );
          clearLoader();
          await handleStartInterviewCancel("other");
          return;
        }
        sessionQuestions.push(...questions);
      } else {
        setLoadingMessage("Loading questions, please wait...");
        const questions =
          await aiInterviewService.getQuestionsBySessionIdAndSessionToken(
            updatedSession._id,
            updatedSession?.sessionToken
          );
        if (!questions || questions.length === 0) {
          console.error(
            "useAiAssessmentStart: No questions found -> handleStartInterviewConfirm"
          );
          openNotification(
            "error",
            "Questions not found",
            "No questions found. Please try again."
          );
          clearLoader();
          await handleStartInterviewCancel("other");
          return;
        }
        sessionQuestions.push(...questions);
      }

      // Formatting Questions for AI Assessment and setting the state
      const formattedQuestions: AIAssessmentQuestion[] = sessionQuestions.map(
        (question: Question) => {
          return {
            sessionId: updatedSession._id.toString(),
            questionId: question._id,
            question: question.description,
            options:
              question.options?.map((option: any) => option.description) ?? [],
            answer: question.responses?.[0]?.description ?? null,
            answerType: question.type.toLowerCase() as
              | "mcq"
              | "descriptive"
              | "fileUpload"
              | "coding",
            fileUrl:
              question.type === "fileUpload"
                ? question.responses?.[0]?.fileUrl ?? null
                : null,
          };
        }
      );

      setCurrentSession(updatedSession);
      localStorage.setItem("sessionToken", updatedSession.sessionToken);
      setAiAssessmentQuestions(formattedQuestions);
      setAiAssessmentDetails((prev) => ({
        ...prev,
        sessionId: updatedSession._id.toString(),
        roundName: prev?.roundName || "",
        noOfQuestions: sessionQuestions.length,
      }));

      try {
        // Validating screen sharing
        if (displayStreamRef.current) {
          const isEntire = isEntireScreenShare(displayStreamRef.current);
          if (!isEntire) {
            console.error(
              "Screen sharing validation failed - not entire screen"
            );
            openNotification(
              "error",
              "Select Entire Screen",
              "Please share your 'Entire Screen' (not a window or tab) to start the interview."
            );

            setShowCamRecorder(false);

            if (cameraMicrophoneStreamRef.current) {
              stopStreamTracks(cameraMicrophoneStreamRef.current);
              cameraMicrophoneStreamRef.current = null;
            }

            if (displayStreamRef.current) {
              stopStreamTracks(displayStreamRef.current);
              displayStreamRef.current = null;
            }
            await handleStartInterviewCancel("other");
            return;
          }
        }

        // Toggling full screen mode
        let fullscreenSuccess = false;
        let retryCount = 0;
        const maxRetries = 5;
        while (!fullscreenSuccess && retryCount < maxRetries) {
          fullscreenSuccess = await toggleToFullscreenMode();
          if (!fullscreenSuccess) {
            retryCount++;
            console.log(
              `useAiAssessmentStart: Fullscreen attempt ${retryCount} failed, retrying in ${TIMER_CONSTANTS.FULLSCREEN_RETRY_DELAY}ms...`
            );
          } else {
            console.log("Successfully entered fullscreen mode");
            setIsFullscreen(true);
            setShowStartAiAssessmentScreen(false);
          }
        }

        // Starting screen recording
        try {
          await startScreenRecording();
        } catch (recErr) {
          console.error(
            "useAiAssessmentStart: Failed to start screen recording:",
            recErr
          );
          setCurrentStep(-1);
          setShowCamRecorder(false);
          openNotification(
            "error",
            "Screen recording failed",
            "Failed to start screen recording. Please try again."
          );
          clearLoader();
          await handleStartInterviewCancel("other");
          return;
        }

        // Closing the start interview modal
        setShowStartAiAssessmentScreen(false);

        // Showing webcam preview
        setShowCamRecorder(true);

        // Start the assessment timer
        setAssessmentStartTime(Date.now());

        // Jumping to the first question
        setCurrentStep(0);
      } catch (error) {
        console.error(
          "useAiAssessmentStart: Error in post-interview setup:",
          error
        );
        clearLoader();
        await handleStartInterviewCancel("other");
        return;
      } finally {
        clearLoader();
      }
    } catch (error: any) {
      console.error("useAiAssessmentStart: Failed to start interview:", error);
      openNotification(
        "error",
        "Interview start failed",
        "Failed to start the interview. Please try again."
      );
      await handleStartInterviewCancel("other");
      clearLoader();
      return;
    } finally {
      clearLoader();
    }
  };

  return {
    toggleToFullscreenMode,
    toggleToNormalScreenMode,
    handleStartAiAssessment,
    handlePermissionCancel,
    handlePermissionAllow,
    handleStartInterviewCancel,
    handleStartInterviewConfirm,
  };
};

export default useAiAssessmentStart;
